<template>
  <v-container>
    <!-- Add Event Button + Dialog -->
    <v-dialog v-model="dialog" max-width="700">
      <template v-slot:activator="{ props: activatorProps }">
        <v-btn
          class="text-none font-weight-regular"
          prepend-icon="mdi-plus"
          text="Add event"
          color="grey-darken-3"
          block
          outlined
          v-bind="activatorProps"
        ></v-btn>
      </template>
      <v-card class="pa-4 text-center">
        <v-card-title>Event Details</v-card-title>
        <v-form ref="form" v-model="valid">
          <v-text-field
            v-model="event.name"
            label="Event Name"
            :rules="nameRules"
            required
          />

          <v-number-input
            v-model="event.max_invitations"
            label="Max invitations"
            controlVariant="stacked"
            :rules="maxInvitationsRules"
            required
          />
          <v-textarea
            v-model="event.description"
            label="Description"
            :rules="descRules"
            required
          />
          <v-row>
            <v-col cols="12">
              <v-date-input
                label="Date"
                color="grey-darken-3"
                v-model="event.date"
              />
            </v-col>
          </v-row>
          <v-file-upload
            density="comfortable"
            variant="comfortable"
            color="grey-darken-3"
            v-model="event.file"
            label="Upload Image"
          />
          <v-file-upload
            density="comfortable"
            variant="comfortable"
            color="grey-darken-3"
            v-model="event.file2"
            label="Upload Image"
            class="my-3"
          />

          <v-checkbox
            v-model="event.status"
            :true-value="1"
            :false-value="0"
            color="success"
            label="Status"
          ></v-checkbox>
          <v-btn @click="createEvent" variant="outlined" block class="ma-2"
            >Submit</v-btn
          >
        </v-form>
      </v-card>
    </v-dialog>

    <v-dialog v-model="editDialog" max-width="700px">
      <v-card class="pa-4 text-center">
        <v-card-title>Edit Event</v-card-title>
        <v-form ref="editForm" v-model="valid">
          <v-text-field
            v-model="selectedEvent.name"
            label="Event Name"
            :rules="nameRules"
            required
          />
          <v-number-input
            v-model="selectedEvent.max_invitations"
            label="Max invitations"
            :rules="maxInvitationsRules"
            required
          />
          <v-textarea
            v-model="selectedEvent.description"
            label="Description"
            :rules="descRules"
            required
          />
          <v-file-upload
            density="comfortable"
            variant="comfortable"
            color="grey-darken-3"
            v-model="selectedEvent.file"
            label="Upload Image"
          />
          <v-file-upload
            density="comfortable"
            variant="comfortable"
            color="grey-darken-3"
            v-model="selectedEvent.file2"
            label="Upload Image"
            class="my-3"
          />

          <v-date-input label="Date" v-model="selectedEvent.date" />

          <v-checkbox
            v-model="selectedEvent.status"
            :true-value="1"
            :false-value="0"
            color="success"
            label="Status"
          ></v-checkbox>

          <v-btn
            class="ma-2"
            color="primary"
            block
            @click="submitEdit"
            :disabled="!valid"
          >
            Save Changes
          </v-btn>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- View Mode Toggle -->
    <v-btn-toggle
      v-model="viewMode"
      class="my-4 pa-0"
      color="grey-darken-3"
      border
      group
    >
      <v-btn
        value="table"
        class="ma-0 pa-2"
        size="small"
        icon="mdi-table"
      ></v-btn>
      <v-btn
        value="cards"
        class="ma-0 pa-2"
        size="small"
        icon="mdi-view-grid-outline"
      ></v-btn>
    </v-btn-toggle>

    <!-- Table View -->
    <template v-if="viewMode === 'table'">
      <v-card variant="outlined" class="pa-2">
        <v-card-title color="grey-darken-3">Events List</v-card-title>
        <v-data-table
          :headers="headers"
          :items="events"
          item-key="id"
          fixed-header
        >
          <template v-slot:[`item.image`]="{ item }">
            <v-img
              :src="'https://sunrise-event.com/uploads/' + item.image"
              max-width="500"
              max-height="400"
              v-if="item.image"
            />
            <v-img
              v-else
              src="https://placehold.co/600x400?text=Event%20Image"
            />
          </template>

          <template v-slot:[`item.image2`]="{ item }">
            <v-img
              :src="'https://sunrise-event.com/uploads/' + item.image2"
              max-width="500"
              max-height="400"
              v-if="item.image2"
            />
            <v-img
              v-else
              src="https://placehold.co/600x400?text=Event%20Image"
            />
          </template>

          <!-- <template v-slot:[`item.qrCode`]="{ item }">
            <v-img
              :src="item.qrCode"
              max-width="100"
              max-height="100"
              v-if="item.qrCode"
            />
            <span v-else>No QR Code</span>
          </template> -->

          <!-- truncate description -->
          <template v-slot:[`item.description`]="{ item }">
            <div class="text-truncate">
              {{
                item.description && item.description.length > 20
                  ? item.description.substring(0, 20) + "..."
                  : item.description
              }}
            </div>
          </template>

          <template v-slot:[`item.status`]="{ item }">
            {{ item.status ? "Active" : "Inactive" }}
          </template>

          <template v-slot:[`item.date`]="{ item }">
            {{ formatDateTime(item.date) }}
          </template>

          <template v-slot:[`item.created_at`]="{ item }">
            {{ formatDateTime(item.created_at) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex justify-end">
              <router-link
                :to="{ name: 'event-detail', params: { id: item.id } }"
              >
                <v-btn
                  color="grey-darken-3"
                  icon="mdi-eye"
                  size="small"
                ></v-btn>
              </router-link>
              <!-- <v-btn
                @click="generateQrCode(item.id, item)"
                icon="mdi-qrcode-scan"
                color="grey-darken-3"
                size="small"
              >
              </v-btn> -->
              <v-btn
                @click="deleteEvent(item.id)"
                icon="mdi-delete"
                color="red"
                size="small"
              ></v-btn>
              <v-btn
                icon="mdi-pencil"
                color="primary"
                size="small"
                @click="openEditDialog(item)"
              ></v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </template>

    <!-- Card View -->
    <template v-else>
      <v-row>
        <v-col v-for="event in events" :key="event.id" cols="12" sm="6" md="4">
          <v-card
            class="ma-3"
            variant="outlined"
            color="grey-darken"
            elevation="3"
          >
            <v-img
              v-if="event.image"
              :src="'https://sunrise-event.com/uploads/' + event.image"
              height="200"
            ></v-img>
            <v-img
              v-else
              src="https://placehold.co/700x400?text=Event%20Image"
              height="200"
            />
            <v-card-title class="text-h6">{{ event.name }}</v-card-title>

            <v-card-text>
              <div class="text-truncate">{{ event.description }}</div>
              <div class="mt-3">
                <v-img
                  v-if="event.qrCode"
                  :src="event.qrCode"
                  max-width="100"
                  max-height="100"
                />
                <span v-else>No QR Code</span>
              </div>
            </v-card-text>
            <v-card-actions>
              <router-link
                :to="{ name: 'event-detail', params: { id: event.id } }"
              >
                <v-btn color="grey-darken-3" small>View</v-btn>
              </router-link>
              <v-btn
                @click="generateQrCode(event.id, event)"
                color="grey-darken-3"
                small
              >
                Generate QR
              </v-btn>
              <v-btn @click="deleteEvent(event.id)" color="red" small
                >Delete</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import axios from "axios";
import QRCode from "qrcode";

export default {
  data() {
    return {
      dialog: false,
      modal2: false,
      valid: false,
      viewMode: "table", // 'table' or 'cards'
      editDialog: false,
      isEditMode: false,
      selectedEvent: {
        id: null,
        name: "",
        description: "",
        date: "",
        file: null,
        file2: null,
        max_invitations: 0,
        status: 0,
      },
      event: {
        qrCode: "",
        name: "",
        description: "",
        date: "",
        file: null,
        file2: null,
        maxInvitations: 0,
        status: 0,
      },

      events: [],
      nameRules: [(v) => !!v || "Name is required"],
      descRules: [(v) => !!v || "Description is required"],
      maxInvitationsRules: [
        (v) => !!v || "Max invitations is required",
        (v) => v > 0 || "Max invitations must be greater than 0",
      ],
      headers: [
        { title: "Image", value: "image" },
        { title: "Image secondaire", value: "image2" },
        // { title: "Code", value: "qrCode" },
        { title: "Event Name", value: "name", sortable: true },
        { title: "Event Description", value: "description" },
        { title: "Max Invitations", value: "max_invitations" },
        { title: "Attendee count", value: "attendee_count", sortable: true },
        { title: "Checked In", value: "checked_in_count" },
        { title: "Status", value: "status" },
        { title: "Event Date", value: "date" },
        { title: "Created at", value: "created_at", sortable: true },
        { title: "Actions", value: "actions" },
      ],
    };
  },
  mounted() {
    this.fetchEvents();
  },
  methods: {
    formatDateTime(dateTime, format = "YYYY-MM-DD HH:mm:ss") {
      if (!dateTime) return "";
      const date = new Date(dateTime);

      const pad = (n) => n.toString().padStart(2, "0");

      const year = date.getFullYear();
      const month = pad(date.getMonth() + 1); // Months are 0-based
      const day = pad(date.getDate());
      const hours = pad(date.getHours());
      const minutes = pad(date.getMinutes());
      const seconds = pad(date.getSeconds());

      if (format === "YYYY-MM-DD") {
        return `${year}-${month}-${day}`;
      }
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
    async fetchEvents() {
      try {
        const response = await axios.get("/api/events");
        this.events = response.data;
      } catch (error) {
        console.error("Error fetching events:", error);
      }
    },
    async createEvent() {
      if (this.$refs.form.validate()) {
        const formData = new FormData();
        formData.append("name", this.event.name);
        formData.append("description", this.event.description);
        formData.append(
          "date",
          this.formatDateTime(this.event.date, "YYYY-MM-DD")
        );

        formData.append("file", this.event.file);
        formData.append("file2", this.event.file2);
        formData.append("max_invitations", this.event.max_invitations);
        formData.append("status", this.event.status);

        try {
          await axios.post("/api/events", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          this.fetchEvents();
          this.dialog = false;
        } catch (error) {
          console.error("Error creating event:", error);
        }
      }
    },
    async deleteEvent(id) {
      try {
        if (!confirm("Are you sure you want to delete this event?")) {
          return;
        }
        await axios.delete(`/api/events/${id}`);

        this.fetchEvents();
      } catch (error) {
        console.error("Error deleting event:", error);
      }
    },
    async generateQrCode(eventId, eventObj) {
      try {
        const qrData = `/events/${eventId}`;
        const qrCodeUrl = await QRCode.toDataURL(qrData);
        eventObj.qrCode = qrCodeUrl;
        await axios.patch(`/api/events/${eventId}`, { qrCode: qrCodeUrl });
        this.fetchEvents();
      } catch (error) {
        console.error("Error generating QR code:", error);
      }
    },
    async updateEvent() {
      try {
        var selectedEvent = this.selectedEvent;
        const formData = new FormData();
        formData.append("name", this.selectedEvent.name);
        formData.append("description", this.selectedEvent.description);
        formData.append("date", this.selectedEvent.date);
        formData.append("status", this.selectedEvent.status);
        if (this.selectedEvent.file) {
          // Only append file if it exists
          formData.append("file", this.selectedEvent.file);
        }
        if (this.selectedEvent.file2) {
          // Only append file if it exists
          formData.append("file2", this.selectedEvent.file2);
        }
        formData.append("max_invitations", this.selectedEvent.max_invitations);
        axios
          .put(`/api/events/${selectedEvent.id}`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          })
          .then((response) => {
            console.log("Event updated successfully", response.data);
            this.editDialog = false;
            this.isEditMode = false;
            this.fetchEvents();
          })
          .catch((error) => {
            console.error("Error updating event:", error);
          });
      } catch (err) {
        console.error("Update failed", err);
      }
    },
    openEditDialog(item) {
      console.log("Opening edit dialog for item:", item);

      this.selectedEvent = { ...item };
      this.selectedEvent.max_invitations = item.max_invitations * 1; // clone the object
      this.editDialog = true;
      this.isEditMode = true;
    },
    submitEdit() {
      if (this.$refs.editForm.validate()) {
        this.updateEvent();
      }
    },
  },
};
</script>

<style scoped>
.v-btn {
  margin: 0 5px;
}
</style>
